import { LiveSttFragment } from '@ftr/contracts/regional-api'
import { assert } from '@ftr/contracts/shared/Assertions'
import { assertUnreachable } from '@ftr/contracts/shared/assertUnreachable'
import { generateUuid, Uuid } from '@ftr/contracts/type/shared'
import { UpsertUtteranceSttEdit } from '@ftr/regional-api-contracts'
import { RealTimeSttRemark, RealTimeSttSegmentMap } from '../../../types'
import { remarkFragmentToSttEditFragment } from '../../../utils'
import { STT_FRAGMENT_SEPARATOR } from '../edit-operations'
import { NoChangeReasonCode, OperationResult } from './operation-result'
import { BoundaryError, checkEditForBoundaryErrors } from './within-boundaries-utils'

export interface MergeRemarkOperation {
  // e.g. enter pressed at end of remark
  type: 'mergeRemark'
  remarkId: Uuid
  direction: 'next' | 'previous'
}

export function mergeRemarkTransaction(
  { remarkId, direction }: MergeRemarkOperation,
  segmentMap: RealTimeSttSegmentMap,
): OperationResult {
  // Find the remark in the segment map
  const remarkAndNeighbours = segmentMap.findRemarkAndNeighbours(remarkId)
  if (!remarkAndNeighbours) {
    throw new Error(`Remark with ID ${remarkId} not found in segment map`)
  }

  let earlierRemarkInMerge: RealTimeSttRemark
  let laterRemarkInMerge: RealTimeSttRemark
  switch (direction) {
    case 'next': {
      if (!remarkAndNeighbours.nextRemark) {
        return {
          type: 'noChange',
          reasonCode: 'merge-no-content-below',
        }
      }

      earlierRemarkInMerge = remarkAndNeighbours.remark
      laterRemarkInMerge = remarkAndNeighbours.nextRemark
      break
    }
    case 'previous': {
      if (!remarkAndNeighbours.previousRemark) {
        return {
          type: 'noChange',
          reasonCode: 'merge-no-content-above',
        }
      }
      earlierRemarkInMerge = remarkAndNeighbours.previousRemark
      laterRemarkInMerge = remarkAndNeighbours.remark
      break
    }
    default:
      assertUnreachable(direction)
  }

  assert(
    earlierRemarkInMerge.fragments && laterRemarkInMerge.fragments,
    'Both remarks involved in merge require fragments to be defined',
  )

  const earlierRemarkFragments = earlierRemarkInMerge.fragments.map(fragment =>
    remarkFragmentToSttEditFragment(fragment, earlierRemarkInMerge.startTime),
  )

  const laterRemarkFragments = laterRemarkInMerge.fragments.map(fragment =>
    remarkFragmentToSttEditFragment(fragment, laterRemarkInMerge.startTime),
  )

  const earlierRemarkFragmentsBeforeLaterStarts = earlierRemarkFragments.filter(fragment => {
    const remarkFirstFragment = laterRemarkFragments?.at(0)
    return !remarkFirstFragment || !fragment.endTime.isAfter(remarkFirstFragment.startTime)
  })

  const laterFragmentsAfterPreviousEnds = laterRemarkFragments!.filter(fragment => {
    const previousLastFragment = earlierRemarkFragments?.at(-1)
    return !previousLastFragment || !fragment.startTime.isBefore(previousLastFragment.endTime)
  })

  const fragmentsToMergeContent = [
    ...earlierRemarkFragments!.slice(earlierRemarkFragmentsBeforeLaterStarts.length),
    ...laterRemarkFragments!.slice(0, laterRemarkFragments.length - laterFragmentsAfterPreviousEnds.length),
  ]

  let mergedContentFragments: LiveSttFragment[] = []
  if (fragmentsToMergeContent.length) {
    const sortedFragments = fragmentsToMergeContent.toSorted((a, b) => a.startTime.compareTo(b.startTime))
    mergedContentFragments = [
      {
        content: fragmentsToMergeContent.map(f => f.content).join(STT_FRAGMENT_SEPARATOR),
        startTime: sortedFragments[0].startTime,
        endTime: sortedFragments[sortedFragments.length - 1].endTime,
      },
    ]
  }

  const appendedFragments = [
    ...earlierRemarkFragmentsBeforeLaterStarts,
    ...mergedContentFragments,
    ...laterFragmentsAfterPreviousEnds,
  ]

  const upsertMergeEdit: UpsertUtteranceSttEdit = {
    type: 'upsertUtterance',
    utteranceId: earlierRemarkInMerge.id,
    dialect: earlierRemarkInMerge.dialect ?? null,
    speakerId: earlierRemarkInMerge.speakerId,
    fragments: appendedFragments,
  }
  // Don't allow merging remarks across inaccessible boundaries, such as sessions or sealed blocks
  const boundaryValidationError = checkEditForBoundaryErrors(upsertMergeEdit, segmentMap)
  if (boundaryValidationError) {
    return {
      type: 'noChange',
      reasonCode: noChangeReasonForBoundaryError(boundaryValidationError),
    }
  }

  return {
    type: 'transaction',
    transaction: {
      id: generateUuid(),
      edits: [
        upsertMergeEdit,
        // delete the second utterance that has been merged
        {
          type: 'deleteUtterance',
          utteranceId: laterRemarkInMerge.id,
        },
      ],
    },
    sideEffects: [
      {
        type: 'focusRemark',
        remarkId: earlierRemarkInMerge.id,
        selectionIndex: earlierRemarkInMerge.content.length + 1, // There is a space added for the next fragment - we want to focus after it
      },
    ],
  }
}

function noChangeReasonForBoundaryError(error: BoundaryError): NoChangeReasonCode {
  switch (error) {
    case 'start-end-cross-session-boundary':
    case 'start-end-not-in-session':
    case 'start-not-in-session':
    case 'end-not-in-session':
      return 'merge-across-session-boundary'
    case 'start-end-cross-hearing-boundary':
      return 'merge-across-hearing-boundary'
    case 'start-end-cross-sealed-boundary':
    case 'end-in-inaccessible-sealed':
    case 'start-end-in-inaccessible-sealed':
    case 'start-in-inaccessible-sealed':
      return 'merge-across-sealed-boundary'
    default:
      assertUnreachable(error)
  }
}
